import React, { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGBarChart, PGPieChart } from '../../components/primitives/PGChart';
import { getBranch, peersOf, INSURERS, FEEDBACK, BRANCHES } from '../../utils/orgData';
import {
  PIPELINE_GROUP,
  PIPELINE_BRANCH,
  repsByBranch,
  fmtRk,
} from '../../utils/opportunityData';
import { PG } from '../../utils/theme';
import OpportunitiesBranchView from './OpportunitiesBranchView';

const { FiFileText, FiTarget, FiDollarSign, FiHeart, FiRepeat, FiTrendingUp, FiAward, FiActivity, FiZap } = FiIcons;

const TabButton = ({ active, onClick, icon: Icon, children }) => (
  <button
    onClick={onClick}
    className={`h-9 px-4 flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] font-bold transition-all ${
      active
        ? 'bg-[#D71921] text-white'
        : 'bg-transparent text-neutral-500 hover:text-white hover:bg-white/5'
    }`}
  >
    {Icon && <Icon className="text-sm" />}
    {children}
  </button>
);

export default function SalesBranchView() {
  const { branchId } = useParams();
  const branch = getBranch(branchId);
  const [view, setView] = useState('sales');

  const jobs = branch?.ops.jobsMTD ?? 0;
  const funnel = useMemo(() => ({
    enquiries: Math.round(jobs * 1.8),
    quotes: Math.round(jobs * 1.35),
    bookings: Math.round(jobs * 1.1),
    completed: jobs,
    paid: Math.round(jobs * 0.92),
  }), [jobs]);

  if (!branch) {
    return <div className="h-full flex items-center justify-center text-neutral-500 font-mono-tech text-xs uppercase tracking-widest">Branch not found</div>;
  }

  const peers = peersOf(branch);
  const peerAvgConversion = peers.length
    ? Math.round(peers.reduce((s, p) => s + p.sales.quoteConversion, 0) / peers.length)
    : branch.sales.quoteConversion;

  const pipeline = PIPELINE_BRANCH[branch.id];
  const groupWinRate = PIPELINE_GROUP.winRate;
  const branchReps = [...repsByBranch(branch.id)].sort((a, b) => b.revenueClosedMTD - a.revenueClosedMTD);

  const funnelSteps = [
    { key: 'Enquiries', val: funnel.enquiries },
    { key: 'Quotes', val: funnel.quotes },
    { key: 'Bookings', val: funnel.bookings },
    { key: 'Completed', val: funnel.completed },
    { key: 'Paid', val: funnel.paid },
  ];

  const scale = branch.ops.jobsMTD / 14000;
  const insurerMix = INSURERS.map((i) => ({
    name: i.name,
    value: Math.max(1, Math.round(i.jobs * scale)),
  }));

  const branchFb = FEEDBACK.filter((f) => f.branch === branch.name);
  const otherFb = FEEDBACK.filter((f) => f.branch !== branch.name);
  const feedback = [...branchFb, ...otherFb].slice(0, 8);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      <div className="flex items-center flex-shrink-0 border-b border-white/5 pb-3">
        <div className="inline-flex border border-white/10">
          <TabButton active={view === 'sales'} onClick={() => setView('sales')} icon={FiActivity}>
            Sales_Perf
          </TabButton>
          <TabButton active={view === 'opps'} onClick={() => setView('opps')} icon={FiZap}>
            Opportunities
          </TabButton>
        </div>
        <div className="ml-auto font-mono-tech text-[9px] uppercase tracking-[0.28em] text-neutral-600">
          {branch.name.toUpperCase()} // {view === 'sales' ? 'REVENUE & CONVERSION' : 'PIPELINE & DEALS'}
        </div>
      </div>

      {view === 'opps' ? (
        <div className="flex-1 min-h-0 overflow-hidden">
          <OpportunitiesBranchView branchId={branchId} />
        </div>
      ) : (
        <>
      <div className="flex items-baseline justify-between flex-shrink-0">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500">Sales / Branch drilldown</div>
          <div className="flex items-baseline gap-3 mt-1">
            <h1 className="font-doto text-3xl text-white">{branch.name}</h1>
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">{branch.town} &middot; {branch.country}</span>
          </div>
        </div>
        <StatusPill tone={branch.sales.quoteConversion >= peerAvgConversion ? 'ok' : 'warn'}>Peer: {branch.peerGroup}</StatusPill>
      </div>

      <div className="grid grid-cols-7 gap-3 flex-shrink-0">
        <KPICard title="QUOTES ISSUED" value={branch.sales.quotesIssued} icon={FiFileText} accent={PG.white} />
        <KPICard title="CONVERSION" value={branch.sales.quoteConversion} unit="%" icon={FiTarget} accent={branch.sales.quoteConversion >= peerAvgConversion ? PG.emerald : PG.amber} target={`${peerAvgConversion}% peer`} />
        <KPICard title="AVG JOB VALUE" value={`R ${(branch.sales.avgJobValue / 1000).toFixed(1)}k`} icon={FiDollarSign} accent={PG.red} />
        <KPICard title="NPS" value={branch.cx.nps} unit="/10" icon={FiHeart} accent={PG.violet} target=">9.0" />
        <KPICard title="REPEAT CUSTOMERS" value={branch.cx.responseRate >= 0 ? branch.sales.repeatCustomerPct : 0} unit="%" icon={FiRepeat} accent={PG.cyan} />
        <KPICard title="BRANCH_PIPELINE" value={fmtRk(pipeline.pipelineValue)} icon={FiTrendingUp} accent={PG.cyan} />
        <KPICard
          title="WIN_RATE"
          value={pipeline.winRate}
          unit="%"
          icon={FiAward}
          accent={pipeline.winRate >= groupWinRate ? PG.emerald : PG.amber}
          target={`${groupWinRate}% grp`}
        />
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <Panel title="QUOTE_FUNNEL" subtitle="MTD" className="col-span-4">
          <div className="h-full p-4 flex flex-col justify-center gap-2">
            {funnelSteps.map((step, i) => {
              const pct = (step.val / funnelSteps[0].val) * 100;
              return (
                <div key={step.key}>
                  <div className="flex justify-between font-mono-tech text-[10px] uppercase tracking-widest">
                    <span className="text-neutral-400">{step.key}</span>
                    <span className="text-white">{step.val.toLocaleString()}</span>
                  </div>
                  <div className="h-2.5 bg-white/5 mt-1">
                    <div
                      className="h-full"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: [PG.white, PG.cyan, PG.violet, PG.amber, PG.emerald][i],
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel title="INSURER_MIX_HERE" subtitle="jobs scaled to branch" className="col-span-3">
          <div className="h-full p-2">
            <PGPieChart data={insurerMix} />
          </div>
        </Panel>

        <Panel title="PEER_CONVERSION_COMPARISON" subtitle={`vs ${peers.length} peers`} className="col-span-2">
          <div className="h-full p-2">
            <PGBarChart
              horizontal
              categories={[branch.name, ...peers.slice(0, 4).map((p) => p.name)].reverse()}
              series={[
                {
                  name: 'Conversion %',
                  data: [
                    { value: branch.sales.quoteConversion, itemStyle: { color: PG.red } },
                    ...peers.slice(0, 4).map((p) => ({ value: p.sales.quoteConversion, itemStyle: { color: PG.neutral500 } })),
                  ].reverse(),
                },
              ]}
              yFormatter={(v) => v + '%'}
            />
          </div>
        </Panel>

        <Panel
          title="REP_LEADERBOARD"
          subtitle={`${branchReps.length} reps // by revenue closed`}
          className="col-span-3"
        >
          <div className="h-full overflow-y-auto custom-scrollbar">
            {branchReps.length === 0 ? (
              <div className="h-full flex items-center justify-center p-4 text-center font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">
                No reps assigned
              </div>
            ) : (
              <table className="w-full text-left">
                <thead className="sticky top-0 bg-[#0A0A0A] z-10">
                  <tr className="border-b border-white/10">
                    <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Rep</th>
                    <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Won</th>
                    <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Pipeline</th>
                  </tr>
                </thead>
                <tbody>
                  {branchReps.map((r) => (
                    <tr key={r.id} className="border-b border-white/5">
                      <td className="px-3 py-2 font-mono-tech text-[11px] uppercase tracking-wider text-white">{r.name}</td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[11px] text-neutral-300">{fmtRk(r.revenueClosedMTD)}</td>
                      <td className="px-3 py-2 text-right font-mono-tech text-[11px] text-neutral-300">{fmtRk(r.pipelineOwned)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Panel>

        <Panel title="NPS_VERBATIM_FEED" subtitle="customer voice // click peer rows to drill" className="col-span-12">
          <div className="h-full overflow-y-auto custom-scrollbar">
            {feedback.map((f, i) => {
              const tone = f.rating >= 9 ? 'ok' : f.rating >= 7 ? 'info' : f.rating >= 5 ? 'warn' : 'alert';
              const isPeer = f.branch !== branch.name;
              const peer = isPeer ? BRANCHES.find((b) => b.name === f.branch) : null;
              const body = (
                <>
                  <div className="flex-shrink-0 w-12 text-center">
                    <div className="font-doto text-2xl" style={{ color: f.rating >= 9 ? PG.emerald : f.rating >= 7 ? PG.cyan : f.rating >= 5 ? PG.amber : PG.red }}>
                      {f.rating}
                    </div>
                    <div className="font-mono-tech text-[8px] uppercase tracking-widest text-neutral-600">/10</div>
                  </div>
                  <div className="flex-1">
                    <div className="text-white text-sm italic">&ldquo;{f.verbatim}&rdquo;</div>
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 mt-1">
                      {f.author} &middot; {f.branch} &middot; {f.ago} ago
                    </div>
                  </div>
                  <StatusPill tone={tone}>{isPeer ? 'Peer' : 'This branch'}</StatusPill>
                </>
              );
              return peer ? (
                <Link
                  key={i}
                  to={`/sliver/sales/branch/${peer.id}`}
                  className="flex items-start gap-4 px-4 py-3 border-b border-white/5 hover:bg-[#D71921]/10 transition-colors"
                >
                  {body}
                </Link>
              ) : (
                <div key={i} className="flex items-start gap-4 px-4 py-3 border-b border-white/5">
                  {body}
                </div>
              );
            })}
          </div>
        </Panel>
      </div>
        </>
      )}
    </motion.div>
  );
}
